import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IChartApi, ISeriesApi, UTCTimestamp } from 'lightweight-charts';
import { Download } from 'lucide-react';
import styles from '../DslAgentTestPage.module.css';
import type {
  ChartTrace,
  DslTag,
  TagExecState,
  TimeSeriesPoint,
  TraceLineStyle,
} from './types';
import { downloadChartAsExcel, hasChartExportData } from './chartExport';
import {
  CHART_PERIODS as CHART_PERIODS_CONST,
  DEFAULT_CHART_PERIOD,
  computeBroadcastRange,
  computeVisibleRangeSeconds,
  type ChartPeriod,
  type VisibleRangeSeconds,
} from './chartPeriod';

interface ChartThemeColors {
  background: string;
  textColor: string;
  grid: string;
  border: string;
}

const DARK_THEME_COLORS: ChartThemeColors = {
  background: '#0a0a0a',
  textColor: 'rgba(255, 255, 255, 0.85)',
  grid: 'rgba(59, 130, 246, 0.12)',
  border: 'rgba(59, 130, 246, 0.25)',
};

const LIGHT_THEME_COLORS: ChartThemeColors = {
  background: '#ffffff',
  textColor: '#223545',
  grid: '#e7eef4',
  border: '#c9d8e4',
};

const readDocumentTheme = (): 'dark' | 'light' => {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
};

const useDocumentTheme = (): 'dark' | 'light' => {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => readDocumentTheme());

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    const target = document.documentElement;
    const observer = new MutationObserver(() => {
      setTheme(readDocumentTheme());
    });
    observer.observe(target, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return theme;
};

export type { ChartTrace, TimeSeriesPoint };

const toLwcLineStyle = (
  lwc: typeof import('lightweight-charts'),
  style: TraceLineStyle,
): number => {
  switch (style) {
    case 'dashed': return lwc.LineStyle.Dashed;
    case 'dotted': return lwc.LineStyle.Dotted;
    default: return lwc.LineStyle.Solid;
  }
};

const parseDateToEpochSeconds = (isoDate: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  const utcMs = Date.UTC(year, month - 1, day);
  if (!Number.isFinite(utcMs)) return null;
  return Math.floor(utcMs / 1000);
};

const toChartPoints = (
  dates: string[],
  values: Array<number | null>,
): TimeSeriesPoint[] => {
  const length = Math.min(dates.length, values.length);
  const points: TimeSeriesPoint[] = [];
  for (let i = 0; i < length; i += 1) {
    const raw = values[i];
    if (raw === null || raw === undefined || !Number.isFinite(raw)) continue;
    const epoch = parseDateToEpochSeconds(dates[i]);
    if (epoch === null) continue;
    points.push({ time: epoch as UTCTimestamp, value: raw });
  }
  points.sort((a, b) => (a.time as number) - (b.time as number));
  return points;
};

const epochToIsoDate = (epoch: number): string =>
  new Date(epoch * 1000).toISOString().slice(0, 10);

export const buildScalarPoints = (
  value: number,
  range: VisibleRangeSeconds,
): TimeSeriesPoint[] => [
  { time: range.fromEpoch, value },
  { time: range.toEpoch, value },
];

export const buildTraces = (
  tags: DslTag[],
  execState: Record<string, TagExecState>,
): ChartTrace[] => {
  const traces: ChartTrace[] = [];
  for (const tag of tags) {
    const state = execState[tag.id];
    if (!state || state.status !== 'ok') continue;
    const result = state.result;
    if (!result) continue;
    if (result.type === 'time_series') {
      traces.push({
        kind: 'series',
        tag,
        points: toChartPoints(result.dates, result.values),
      });
    } else if (result.type === 'scalar' && Number.isFinite(result.value)) {
      traces.push({ kind: 'scalar', tag, value: result.value });
    }
  }
  return traces;
};

export interface TooltipRow {
  tagId: string;
  color: string;
  date: string;
  value: number;
  isScalar?: boolean;
}

export const buildTooltipRows = (
  tags: DslTag[],
  execState: Record<string, TagExecState>,
  time: number,
): TooltipRow[] => {
  if (!Number.isFinite(time)) return [];
  const traces = buildTraces(tags, execState);
  const rows: TooltipRow[] = [];
  for (const trace of traces) {
    if (trace.kind === 'scalar') {
      rows.push({
        tagId: trace.tag.id,
        color: trace.tag.color,
        date: epochToIsoDate(time),
        value: trace.value,
        isScalar: true,
      });
      continue;
    }
    const hit = trace.points.find((p) => (p.time as number) === time);
    if (!hit) continue;
    rows.push({
      tagId: trace.tag.id,
      color: trace.tag.color,
      date: epochToIsoDate(hit.time as number),
      value: hit.value,
    });
  }
  return rows;
};

const formatTooltipValue = (value: number): string => {
  if (!Number.isFinite(value)) return String(value);
  const abs = Math.abs(value);
  if (abs !== 0 && (abs < 0.0001 || abs >= 1e12)) return value.toExponential(4);
  return value.toFixed(4);
};

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderTooltipHtml = (rows: TooltipRow[]): string => {
  const items = rows
    .map((row) => {
      const date = escapeHtml(row.date);
      const valueText = formatTooltipValue(row.value);
      const value = escapeHtml(valueText);
      const color = escapeHtml(row.color);
      return (
        `<div class="dsl-chart-tooltip-row" data-tag-id="${escapeHtml(row.tagId)}">` +
        `<span class="dsl-chart-tooltip-swatch" style="background:${color}"></span>` +
        `<span class="dsl-chart-tooltip-date">${date}</span>` +
        `<span class="dsl-chart-tooltip-value">${value}</span>` +
        `</div>`
      );
    })
    .join('');
  return items;
};

export const CHART_PERIODS = CHART_PERIODS_CONST;
export type { ChartPeriod };

export interface DslResultChartProps {
  tags: DslTag[];
  execState: Record<string, TagExecState>;
  resetToken: number;
  hiddenTagIds?: ReadonlySet<string>;
  compact?: boolean;
  externalError?: string | null;
  viewPeriod?: ChartPeriod;
}

const EMPTY_HIDDEN: ReadonlySet<string> = new Set();

const applyVisibleRange = (
  chart: IChartApi,
  traces: ChartTrace[],
  period: ChartPeriod,
): void => {
  const timeScale = chart.timeScale();
  const range = computeVisibleRangeSeconds(traces, period);
  if (range === null) {
    timeScale.fitContent();
    return;
  }
  timeScale.setVisibleRange({ from: range.fromEpoch, to: range.toEpoch });
};

export const DslResultChart: React.FC<DslResultChartProps> = ({
  tags,
  execState,
  resetToken,
  hiddenTagIds = EMPTY_HIDDEN,
  compact = false,
  externalError = null,
  viewPeriod = DEFAULT_CHART_PERIOD,
}) => {
  const theme = useDocumentTheme();
  const themeColors = theme === 'light' ? LIGHT_THEME_COLORS : DARK_THEME_COLORS;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesByTagIdRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());
  const lwcModuleRef = useRef<typeof import('lightweight-charts') | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const themeColorsRef = useRef<ChartThemeColors>(themeColors);
  themeColorsRef.current = themeColors;

  const traces = useMemo(
    () => buildTraces(tags, execState),
    [tags, execState],
  );

  const canDownload = useMemo(
    () => hasChartExportData(tags, execState),
    [tags, execState],
  );

  const handleDownload = useCallback(() => {
    downloadChartAsExcel(tags, execState);
  }, [tags, execState]);

  const latestTracesRef = useRef<ChartTrace[]>(traces);
  latestTracesRef.current = traces;
  const tagsRef = useRef<DslTag[]>(tags);
  tagsRef.current = tags;
  const execStateRef = useRef<Record<string, TagExecState>>(execState);
  execStateRef.current = execState;
  const hiddenRef = useRef<ReadonlySet<string>>(hiddenTagIds);
  hiddenRef.current = hiddenTagIds;
  const viewPeriodRef = useRef<ChartPeriod>(viewPeriod);
  viewPeriodRef.current = viewPeriod;

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') return undefined;

    let cancelled = false;

    const init = async () => {
      const mount = containerRef.current;
      if (!mount) return;
      try {
        const lwc = await import('lightweight-charts');
        if (cancelled || !containerRef.current) return;

        const colors = themeColorsRef.current;
        const chart = lwc.createChart(containerRef.current, {
          autoSize: true,
          layout: {
            background: { type: lwc.ColorType.Solid, color: colors.background },
            textColor: colors.textColor,
          },
          grid: {
            vertLines: { color: colors.grid },
            horzLines: { color: colors.grid },
          },
          crosshair: { mode: lwc.CrosshairMode.Normal },
          rightPriceScale: { borderColor: colors.border },
          timeScale: {
            borderColor: colors.border,
            timeVisible: false,
            rightOffset: 3,
          },
          localization: { locale: 'en-US' },
        });

        chartRef.current = chart;
        lwcModuleRef.current = lwc;

        const initialBroadcast = computeBroadcastRange(
          latestTracesRef.current,
          viewPeriodRef.current,
        );
        for (const trace of latestTracesRef.current) {
          const isScalar = trace.kind === 'scalar';
          const lineStyle: TraceLineStyle = isScalar ? 'dashed' : trace.tag.lineStyle;
          const series = chart.addSeries(lwc.LineSeries, {
            color: trace.tag.color,
            lineWidth: trace.tag.lineWidth,
            lineStyle: toLwcLineStyle(lwc, lineStyle),
            priceLineVisible: false,
            lastValueVisible: true,
            visible: !hiddenRef.current.has(trace.tag.id),
            title: '',
          });
          if (trace.kind === 'series') {
            series.setData(trace.points);
          } else if (initialBroadcast !== null) {
            series.setData(buildScalarPoints(trace.value, initialBroadcast));
          }
          seriesByTagIdRef.current.set(trace.tag.id, series);
        }

        chart.subscribeCrosshairMove((param) => {
          const tooltip = tooltipRef.current;
          const mount = containerRef.current;
          if (!tooltip || !mount) return;
          if (
            !param.point ||
            param.point.x < 0 ||
            param.point.y < 0 ||
            typeof param.time !== 'number'
          ) {
            tooltip.style.display = 'none';
            return;
          }
          const hidden = hiddenRef.current;
          const visibleTags = tagsRef.current.filter((t) => !hidden.has(t.id));
          const rows = buildTooltipRows(
            visibleTags,
            execStateRef.current,
            param.time as number,
          );
          if (rows.length === 0) {
            tooltip.style.display = 'none';
            return;
          }
          tooltip.innerHTML = renderTooltipHtml(rows);
          tooltip.style.display = 'block';
          const rect = mount.getBoundingClientRect();
          const offsetX = param.point.x + 14;
          const offsetY = param.point.y + 14;
          const tipWidth = tooltip.offsetWidth;
          const tipHeight = tooltip.offsetHeight;
          const clampedX = Math.max(
            4,
            Math.min(offsetX, Math.max(4, rect.width - tipWidth - 8)),
          );
          const clampedY = Math.max(
            4,
            Math.min(offsetY, Math.max(4, rect.height - tipHeight - 8)),
          );
          tooltip.style.left = `${clampedX}px`;
          tooltip.style.top = `${clampedY}px`;
        });

        applyVisibleRange(chart, latestTracesRef.current, viewPeriodRef.current);
        setInitError(null);
      } catch {
        setInitError('Unable to initialize chart.');
      }
    };

    init();

    return () => {
      cancelled = true;
      seriesByTagIdRef.current.clear();
      chartRef.current?.remove();
      chartRef.current = null;
      lwcModuleRef.current = null;
    };
  }, [resetToken, theme]);

  useEffect(() => {
    const chart = chartRef.current;
    const lwc = lwcModuleRef.current;
    if (!chart || !lwc) return;

    const seriesMap = seriesByTagIdRef.current;
    const desiredIds = new Set(traces.map((t) => t.tag.id));

    for (const [id, series] of seriesMap) {
      if (!desiredIds.has(id)) {
        chart.removeSeries(series);
        seriesMap.delete(id);
      }
    }

    const broadcastRange = computeBroadcastRange(traces, viewPeriod);

    for (const trace of traces) {
      const hidden = hiddenTagIds.has(trace.tag.id);
      const isScalar = trace.kind === 'scalar';
      const lineStyle: TraceLineStyle = isScalar ? 'dashed' : trace.tag.lineStyle;
      let series = seriesMap.get(trace.tag.id);
      if (!series) {
        series = chart.addSeries(lwc.LineSeries, {
          color: trace.tag.color,
          lineWidth: trace.tag.lineWidth,
          lineStyle: toLwcLineStyle(lwc, lineStyle),
          priceLineVisible: false,
          lastValueVisible: true,
          visible: !hidden,
          title: '',
        });
        seriesMap.set(trace.tag.id, series);
      } else {
        series.applyOptions({
          color: trace.tag.color,
          lineWidth: trace.tag.lineWidth,
          lineStyle: toLwcLineStyle(lwc, lineStyle),
          visible: !hidden,
          title: '',
        });
      }
      if (trace.kind === 'series') {
        series.setData(trace.points);
      } else if (broadcastRange !== null) {
        series.setData(buildScalarPoints(trace.value, broadcastRange));
      } else {
        series.setData([]);
      }
    }
  }, [traces, hiddenTagIds, viewPeriod]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    applyVisibleRange(chart, traces, viewPeriod);
  }, [traces, viewPeriod]);

  const sectionClassName = compact
    ? `${styles.chartSection} ${styles.chartSectionCompact}`
    : styles.chartSection;

  return (
    <section className={sectionClassName} data-testid="dsl-agent-result">
      {compact ? null : (
        <div className={styles.chartSectionHeader}>
          <div className={styles.chartSectionHeaderRow}>
            <h2>DSL Result Chart</h2>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={handleDownload}
              disabled={!canDownload}
              aria-label="Download chart data as Excel"
              data-testid="dsl-agent-chart-download-button"
            >
              <Download size={16} aria-hidden="true" />
              <span>Download Excel</span>
            </button>
          </div>
          <p>
            {traces.length} trace{traces.length === 1 ? '' : 's'} rendered (text, null, and error tags are excluded).
          </p>
        </div>
      )}
      <div className={styles.chartMount}>
        <div
          ref={containerRef}
          className={styles.chartContainer}
          data-testid="dsl-agent-chart"
          data-trace-count={traces.length}
        />
        <div
          ref={tooltipRef}
          className={styles.chartTooltip}
          data-testid="dsl-agent-chart-tooltip"
          role="status"
          aria-live="polite"
          style={{ display: 'none' }}
        />
        {traces.length === 0 ? (
          <p className={styles.chartEmpty} data-testid="dsl-agent-chart-empty">
            No time-series data yet. Add a tag whose DSL produces a time series and submit.
          </p>
        ) : null}
      </div>
      {initError ? (
        <p className={styles.chartError} role="alert">
          {initError}
        </p>
      ) : null}
      {externalError ? (
        <p
          className={styles.chartError}
          role="alert"
          data-testid="dsl-agent-chart-external-error"
        >
          {externalError}
        </p>
      ) : null}
    </section>
  );
};
